import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ExternalLink, Hash, ListChecks, Save, ShieldCheck, Workflow } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { Select } from '../../components/Controls';
import Loader from '../../components/Loader';

// HR Request Center > Settings (Stephanet's custom/hrrc/Settings.jsx). Number format, required fields and SLA days per
// type are kept here; a type's approval route is its approval-engine workflow, so it is shown read-only with a link to
// Workflow Config, and Permission Setup opens the Permission Matrix (HR Request Center group).

const WORKFLOW_CONFIG = '/admin?tab=workflows';
const PERMISSION_MATRIX = '/admin/user-access/permissions';

export default function Settings({ onSaved }) {
  const { notify } = useHR();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [fmt, setFmt] = useState({ pattern: '', draft_pattern: '' });
  const [open, setOpen] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => hr.hrrcSettings().then(x => { setS(x); setFmt(x.number_format); }).catch(e => notify(e.message, 'bad'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!s) return <Loader title="Loading settings…" />;
  const can = s.can.configure;

  const saveFormat = async () => {
    setBusy(true);
    try { const r = await hr.hrrcSaveNumberFormat(fmt); notify(`${r.message} — next number looks like ${r.sample}`); await load(); onSaved?.(); }
    catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  // one PUT per changed type; the modal closes once all are saved
  const saveTypes = async changed => {
    setBusy(true);
    try {
      for (const [id, body] of Object.entries(changed)) await hr.hrrcSaveType(id, body);
      notify(`${Object.keys(changed).length} request type${Object.keys(changed).length === 1 ? '' : 's'} saved`);
      setOpen(''); await load(); onSaved?.();
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };

  return (
    <>
      {!can && <p className="hrc-warn">You can view these settings; changing them needs Configure rights in the Request Center.</p>}
      <div className="hrc-settings">
        <div className="mdr-card hrc-set-card">
          <h3><Hash size={16} /> Request Number Format</h3>
          <div className="hrc-form">
            <label className="hrc-f"><span>Format Pattern</span><input value={fmt.pattern} disabled={!can} onChange={e => setFmt(f => ({ ...f, pattern: e.target.value.toUpperCase() }))} /></label>
            <label className="hrc-f"><span>Draft Pattern</span><input value={fmt.draft_pattern} disabled={!can} onChange={e => setFmt(f => ({ ...f, draft_pattern: e.target.value.toUpperCase() }))} /></label>
          </div>
          <p className="hrc-sub">YYYY / YY = year, MM = month, DD = day, a run of zeros = the counter (e.g. REQ-YYYY-MM-0000).</p>
          {can && <button className="mds-run" disabled={busy} onClick={saveFormat}><Save size={13} /> Save Format</button>}
        </div>
        <div className="mdr-card hrc-set-card">
          <h3><ListChecks size={16} /> Required Fields Setup</h3>
          <div className="hrc-set-row"><p>Configure required fields by request type</p><button className="mdr-btn" onClick={() => setOpen('fields')}>Configure Fields</button></div>
        </div>
        <div className="mdr-card hrc-set-card">
          <h3><Workflow size={16} /> Approval Workflow Mapping</h3>
          <div className="hrc-set-row"><p>Each type's approval route and its SLA (working days) per priority</p><button className="mdr-btn" onClick={() => setOpen('workflow')}>Configure Workflows</button></div>
        </div>
        <div className="mdr-card hrc-set-card">
          <h3><ShieldCheck size={16} /> Permission Setup</h3>
          <div className="hrc-set-row"><p>Create, View All and Configure are granted per role in the Permission Matrix</p>
            <button className="mdr-btn" onClick={() => navigate(PERMISSION_MATRIX)}><ExternalLink size={13} /> Permission Matrix</button></div>
        </div>
      </div>
      {open === 'fields' && <FieldsModal s={s} can={can} busy={busy} onClose={() => setOpen('')} onSave={saveTypes} />}
      {open === 'workflow' && <WorkflowModal s={s} can={can} busy={busy} onClose={() => setOpen('')} onSave={saveTypes} onConfig={() => navigate(WORKFLOW_CONFIG)} />}
    </>
  );
}

function useTypeRows(s, mod) {
  return useMemo(() => s.modules.filter(m => mod === 'All' || m.code === mod)
    .flatMap(m => m.types.map(t => ({ key: String(t.id), module: m.label, t }))), [s, mod]);
}

function ModuleFilter({ s, mod, setMod }) {
  return (
    <Select className="hrc-modsel" value={mod} onChange={e => setMod(e.target.value)} title="Module">
      <option value="All">All modules</option>{s.modules.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
    </Select>
  );
}

function FieldsModal({ s, can, busy, onClose, onSave }) {
  const [mod, setMod] = useState('All');
  const [req, setReq] = useState(() => Object.fromEntries(s.modules.flatMap(m => m.types).map(t => [String(t.id), t.fields.filter(f => f.required).map(f => f.key)])));
  const rows = useTypeRows(s, mod);
  const toggle = (k, f) => setReq(r => ({ ...r, [k]: r[k].includes(f) ? r[k].filter(x => x !== f) : [...r[k], f] }));
  const save = () => {
    const changed = {};
    s.modules.flatMap(m => m.types).forEach(t => {
      const was = t.fields.filter(f => f.required).map(f => f.key).sort().join('|');
      if (req[t.id].slice().sort().join('|') !== was) changed[t.id] = { required: req[t.id] };
    });
    return Object.keys(changed).length ? onSave(changed) : onClose();
  };
  return (
    <Modal wide className="hrc-set-modal" title="Required Fields Setup" onClose={onClose}
           footer={<><span className="wh-spacer" /><button className="mdr-btn" onClick={onClose}>Close</button>
             {can && <button className="mds-run" disabled={busy} onClick={save}><Save size={13} /> Save Changes</button>}</>}>
      <div className="hrc-set-head"><p>Configure which fields are required for each request type. Click a field to switch Required / Optional.</p><ModuleFilter s={s} mod={mod} setMod={setMod} /></div>
      <div className="mdr-scroll"><table className="mdr-table mds-table hrc-set-table">
        <thead><tr><th>Module</th><th>Request Type</th><th>Fields (Required / Optional)</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.key}><td className="b">{r.module}</td><td className="b">{r.t.name}</td>
            <td><div className="hrc-fieldchips">{r.t.fields.map(f => (
              <button key={f.key} type="button" disabled={!can} className={`hrc-fchip ${req[r.key].includes(f.key) ? 'on' : ''}`} onClick={() => toggle(r.key, f.key)}>
                {f.label} <small>{req[r.key].includes(f.key) ? 'Required' : 'Optional'}</small>
              </button>))}</div></td></tr>
        ))}</tbody>
      </table></div>
    </Modal>
  );
}

const HOME_ROUTE = { leave: 'Leave Management', attendance_correction: 'Attendance Edit Log', hr_case: 'HR case' };

function WorkflowModal({ s, can, busy, onClose, onSave, onConfig }) {
  const [mod, setMod] = useState('All');
  const prios = s.priorities.map(p => p.name);
  const [sla, setSla] = useState(() => Object.fromEntries(s.modules.flatMap(m => m.types).map(t => [String(t.id), Object.fromEntries(prios.map(p => [p, t.sla[p] ?? '']))])));
  const rows = useTypeRows(s, mod);
  const save = () => {
    const changed = {};
    s.modules.flatMap(m => m.types).forEach(t => {
      const out = {};
      prios.forEach(p => { const v = sla[t.id][p]; if (v !== '' && Number(v) !== t.sla[p]) out[p] = Number(v); });
      if (Object.keys(out).length) changed[t.id] = { sla: out };
    });
    if (Object.values(changed).some(c => Object.values(c.sla).some(v => !Number.isInteger(v) || v < 0))) return null;
    return Object.keys(changed).length ? onSave(changed) : onClose();
  };
  return (
    <Modal wide className="hrc-set-modal" title="Approval Workflow Mapping" onClose={onClose}
           footer={<><button className="mdr-btn" onClick={onConfig}><ExternalLink size={13} /> Edit routes in Workflow Config</button><span className="wh-spacer" />
             <button className="mdr-btn" onClick={onClose}>Close</button>{can && <button className="mds-run" disabled={busy} onClick={save}><Save size={13} /> Save Changes</button>}</>}>
      <div className="hrc-set-head"><p>Approval routes run in the approval engine and are edited in Workflow Config. SLA (working days) per priority is set here.</p><ModuleFilter s={s} mod={mod} setMod={setMod} /></div>
      <div className="mdr-scroll"><table className="mdr-table mds-table hrc-set-table">
        <thead><tr><th>Module</th><th>Request Type</th><th>Approval Chain</th>{prios.map(p => <th key={p} className="c">SLA {p}</th>)}</tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.key}><td className="b">{r.module}</td><td className="b">{r.t.name}</td>
            <td>
              <div className="hrc-route">{r.t.route.length
                ? <div>{r.t.route.map((x, i) => <span key={x + i}><span className="hrc-route-chip">{x}</span>{i < r.t.route.length - 1 && <ChevronRight size={12} />}</span>)}</div>
                : <span className="muted">No active workflow</span>}</div>
              {HOME_ROUTE[r.t.home] && <small className="muted">Decided by the {HOME_ROUTE[r.t.home]} approval</small>}
            </td>
            {prios.map(p => <td key={p} className="c"><input type="number" min="0" step="1" className="hrc-sla-in" disabled={!can} value={sla[r.key][p]} placeholder="—"
              onChange={e => setSla(w => ({ ...w, [r.key]: { ...w[r.key], [p]: e.target.value } }))} /></td>)}
          </tr>))}</tbody>
      </table></div>
      <p className="hrc-sub">A blank SLA falls back to the priority default ({s.priorities.map(p => `${p.name} ${p.sla}`).join(', ')}; 0 = same day).</p>
    </Modal>
  );
}
